import { Controller } from "@hotwired/stimulus"
import * as echarts from "echarts/core"
import { LineChart } from "echarts/charts"
import { GridComponent, TooltipComponent } from "echarts/components"
import { CanvasRenderer } from "echarts/renderers"

echarts.use([LineChart, GridComponent, TooltipComponent, CanvasRenderer])

export default class extends Controller {
  static values = {
    series: Array,
    labels: { type: Array, default: [] },
    max: { type: Number, default: 100 },
    axis: { type: Boolean, default: false },
    unit: { type: String, default: "%" },
  }

  connect() {
    this.chart = echarts.init(this.element, null, { renderer: "canvas" })
    this.render()

    this.resizeObserver = new ResizeObserver(() => this.chart.resize())
    this.resizeObserver.observe(this.element)

    this.onThemeChange = () => this.render()
    window.addEventListener("afagh:theme-changed", this.onThemeChange)
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    window.removeEventListener("afagh:theme-changed", this.onThemeChange)
    this.chart?.dispose()
  }

  resolveColor(color) {
    if (!color?.startsWith("--")) return color
    return getComputedStyle(document.documentElement).getPropertyValue(color).trim()
  }

  formatValue(value) {
    if (this.unitValue === "%") return `${value}%`
    return this.unitValue ? `${value} ${this.unitValue}` : `${value}`
  }

  render() {
    const axis = this.axisValue
    const border = this.resolveColor("--border")
    const muted = this.resolveColor("--muted")

    this.chart.setOption({
      animation: false,
      grid: axis ? { left: 34, right: 10, top: 10, bottom: 22 } : { left: 0, right: 0, top: 4, bottom: 0 },
      xAxis: {
        type: "category",
        show: axis,
        boundaryGap: false,
        data: this.labelsValue,
        axisLine: { lineStyle: { color: border } },
        axisTick: { show: false },
        axisLabel: { color: muted, fontSize: 10 },
      },
      yAxis: {
        type: "value",
        show: axis,
        min: 0,
        max: this.maxValue > 0 ? this.maxValue : undefined,
        splitLine: { lineStyle: { color: border } },
        axisLabel: { color: muted, fontSize: 10, formatter: (v) => this.formatValue(v) },
      },
      tooltip: {
        trigger: "axis",
        confine: true,
        valueFormatter: (v) => this.formatValue(v),
      },
      series: this.seriesValue.map((s) => ({
        type: "line",
        data: s.data,
        showSymbol: false,
        lineStyle: { width: 1.8, color: this.resolveColor(s.color), opacity: s.opacity ?? 1 },
        areaStyle: s.area ? { color: this.resolveColor(s.color), opacity: 0.12 } : undefined,
      })),
    }, true)
  }
}
